/**
 * Open Bets 共享类型和工具函数
 */
import { copyToClipboard } from '@solid-primitives/clipboard';
import { useCurrency, walletSettings } from '@bz/common';
import { getCoinIcon, isFiat, isBonus } from '@bz/wallet/src/utils';
import { SportIcon } from '@bz/systemAssets';
import { Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useOptimizedMatchDetail } from '@bz/betSlip/src/betslip/matchDetailsStore';
import { ApiResponse, SportCategoryMatch } from '@bz/request/src/apis';
import { useSocketBusiness } from '@bz/socket';
import { createDerivedStaticStore } from '@solid-primitives/static-store';
import { merge } from 'lodash-es';
import Decimal from 'decimal.js-light';

import { BetSlipType, SingleSubType } from '../../types';
import { Tooltip } from '../components/Tooltip';

import currencyConvertHint from '~/utils/formatCurrency';
import { getSportTypeById } from '~/utils';
import { useMatchFormatTime } from '~/primitives/useMatchFormatTime';
import { EventStatus } from '~/constants';
import { getSportsRadarLang, toTrailingDigits, checkEventStatus, replaceOutcomesPlaceholders } from '~/utils';

// 类型定义
export type BetTicket = ApiResponse<'getUserTicketsPage'>['betTickets'][number];
export type BetSelection = ApiResponse<'getUserTicketsPage'>['betSelections'][number] & {
  colName?: string;
};

// 结算状态枚举
export enum SettledStatus {
  OPEN = 0,
  LOSE = 10,
  WIN = 20,
  HALF_WIN = 30,
  HALF_LOSE = 40,
  PUSH = 50,
  CANCELLED = 60,
  CASHED_OUT = 70,
  PARTIAL_RETURN = 55,
  REFUND = 80,
}

// 状态显示配置
export const getStatusDisplay = (status: SettledStatus) => {
  switch (status) {
    case SettledStatus.OPEN:
      return { text: t('Open'), color: 'text-function-open', bgColor: 'bg-[rgba(64,159,250,0.1)]' };
    case SettledStatus.LOSE:
      return { text: t('Lost'), color: 'text-function-error', bgColor: 'bg-[rgba(244,48,28,0.1)]' };
    case SettledStatus.WIN:
      return { text: t('Won'), color: 'text-brand-default', bgColor: 'bg-[rgba(0,220,147,0.1)]' };
    case SettledStatus.HALF_WIN:
      return { text: t('Half Win'), color: 'text-brand-default', bgColor: 'bg-[rgba(0,220,147,0.1)]' };
    case SettledStatus.HALF_LOSE:
      return { text: t('Half Lose'), color: 'text-function-error', bgColor: 'bg-[rgba(244,48,28,0.1)]' };
    case SettledStatus.PUSH:
      return { text: t('Push'), color: 'text-function-alert', bgColor: 'bg-[rgba(250,204,21,0.1)]' };
    case SettledStatus.CANCELLED:
      return { text: t('Cancelled'), color: 'text-neutral-main3', bgColor: 'bg-[rgba(163,190,204,0.1)]' };
    case SettledStatus.CASHED_OUT:
      return { text: t('Cashed Out'), color: 'text-function-highlight', bgColor: 'bg-[rgba(250,204,21,0.1)]' };
    case SettledStatus.REFUND:
      return { text: t('Refund'), color: 'text-brand-default', bgColor: 'bg-[rgba(0,220,147,0.1)]' };
    case SettledStatus.PARTIAL_RETURN:
      return { text: t('Partial Return'), color: 'text-brand-default', bgColor: 'bg-[rgba(0,220,147,0.1)]' };
    default:
      return { text: t('Unknown'), color: 'text-neutral-main3', bgColor: 'bg-[rgba(163,190,204,0.1)]' };
  }
};

// 状态筛选选项
export const statusFilters = [
  { label: () => t('All'), slug: 'all' as const, value: [-1] },
  { label: () => t('Open Bets'), slug: 'open-bets' as const, value: [SettledStatus.OPEN] },
  { label: () => t('Won'), slug: 'won' as const, value: [SettledStatus.WIN, SettledStatus.HALF_WIN] },
  { label: () => t('Lost'), slug: 'lost' as const, value: [SettledStatus.LOSE, SettledStatus.HALF_LOSE] },
  { label: () => t('Cancelled'), slug: 'cancelled' as const, value: [SettledStatus.CANCELLED] },
  { label: () => t('Cashed Out'), slug: 'cashed-out' as const, value: [SettledStatus.CASHED_OUT] },
  { label: () => t('Refund'), slug: 'refund' as const, value: [SettledStatus.REFUND] },
];

// 获取订单类型显示名称
export const getBetTypeDisplay = (betType: string) => {
  switch (betType) {
    case BetSlipType.Single:
      return t('Single');
    case BetSlipType.Combo:
      return t('Combo');
    case BetSlipType.System:
      return t('System');
    case SingleSubType.CustomBet:
      return t('Custom Bet');
    case SingleSubType.Outright:
      return t('Single (Outright)');
    default:
      return betType;
  }
};

// 判断订单类型
export const isSingleType = (betType: string) => betType === BetSlipType.Single;
export const isComboType = (betType: string) => betType === BetSlipType.Combo;
export const isSystemType = (betType: string) => betType === BetSlipType.System;
export const isCustomBetType = (betType: string) => betType === SingleSubType.CustomBet;
export const isOutrightType = (betType: string) => betType === SingleSubType.Outright;

// 格式化创建时间
export const formatCreateTime = (timestamp: number) => {
  const date = new Date(Number(timestamp));
  return date.toLocaleDateString(setting.lang, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// 格式化比赛时间（与 createRelativeTime 格式一致）
// 返回格式如: "Tomorrow 04:00", "Today 14:30", "Mon, Dec 18 20:00"
export const formatMatchTime = (timestamp: number) => {
  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;

  const date = new Date(Number(timestamp));
  const now = new Date();
  const difference = Number(timestamp) - now.getTime();
  const absDiff = Math.abs(difference);
  const isPast = difference < 0;

  // 格式化时间部分
  const formatTime = (d: Date) =>
    d.toLocaleTimeString(setting.lang, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });

  // 格式化完整日期
  const formatFullDate = (d: Date) => {
    const datePart = d.toLocaleDateString(setting.lang, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour12: false,
    });
    return `${datePart} ${formatTime(d)}`;
  };

  // 如果是过去时间，显示完整日期
  if (isPast) {
    return { date: formatFullDate(date), time: '' };
  }

  // 小于1分钟 - 显示 "Started"
  if (absDiff < MINUTE) {
    return { date: t('Started'), time: '' };
  }

  // 小于2小时 - 显示倒计时
  if (absDiff < 2 * HOUR) {
    const hours = Math.floor(absDiff / HOUR);
    const minutes = Math.floor((absDiff % HOUR) / MINUTE);
    return { date: t('Today'), time: `${hours}h ${minutes}'` };
  }

  // 同一天 - 显示 "Today + 时间"
  if (date.toDateString() === now.toDateString()) {
    return { date: t('Today'), time: formatTime(date) };
  }

  // 明天 - 显示 "Tomorrow + 时间"
  const tomorrow = new Date(now.getTime() + DAY);
  if (date.toDateString() === tomorrow.toDateString()) {
    return { date: t('Tomorrow'), time: formatTime(date) };
  }

  // 其他情况 - 显示完整日期
  return { date: formatFullDate(date), time: '' };
};

// 共享 Props 类型
export interface BetCardBaseProps {
  ticket: BetTicket;
  selections: BetSelection[];
  onCashOut?: (ticketNo: string) => void;
}

/**
 * 比赛时间展示组件
 * 接收 matchDetail，格式化并展示比赛时间信息
 */
export const MatchTimeDisplay = (props: { matchDetail: SportCategoryMatch | undefined }) => {
  const { useMatchPush } = useSocketBusiness();
  const matchId = createMemo(() => props.matchDetail?.id || '');
  // 比赛实时数据推送
  const matchWs = useMatchPush(matchId);

  const match = createDerivedStaticStore<SportCategoryMatch>(() => {
    return merge({}, Object.assign({}, props.matchDetail), matchWs());
  });

  const formatTime = useMatchFormatTime(match, getSportTypeById(String(props.matchDetail?.sportId)));
  const upcomingTimeParts = createMemo(() => formatTime().split('\n'));
  const upcomingDate = createMemo(() => upcomingTimeParts()[0] || '');
  const upcomingClock = createMemo(() => upcomingTimeParts()[1] || '');

  return (
    <Show when={match}>
      <Show when={match?.status === EventStatus.LIVE_EVENTS}>
        <span class="caption-s-medium text-brand-default">{match?.matchStatusName || ''}</span>
        <span class="caption-s-medium text-brand-default">{formatTime()}</span>
      </Show>
      <Show when={match?.status === EventStatus.UPCOMING}>
        <span class="caption-s-medium text-neutral-main2">{upcomingDate()}</span>
        <Show when={upcomingClock()}>
          <span class="caption-s-medium text-neutral-main1_alt">{upcomingClock()}</span>
        </Show>
      </Show>
      <Show when={match?.status !== EventStatus.LIVE_EVENTS && match?.status !== EventStatus.UPCOMING}>
        <span class="caption-s-medium text-neutral-main2">{match?.matchStatusName || ''}</span>
      </Show>
    </Show>
  );
};

/**
 * 状态栏组件 - 所有订单类型共用
 * 显示订单类型、创建时间和状态标签
 */
export const StatusBar = (props: { betType: string; createTime: number; settledStatus: number }) => {
  const statusDisplay = () => getStatusDisplay(props.settledStatus as SettledStatus);

  return (
    <div
      class="flex items-center justify-between"
      style={{
        height: '22px',
      }}
    >
      <div class="flex items-center gap-1.5">
        <span class="caption-s-semibold text-neutral-main1">{getBetTypeDisplay(props.betType)}</span>
        <span class="caption-s-medium text-neutral-main2">{formatCreateTime(props.createTime)}</span>
      </div>
      <div
        class={cx(
          'caption-s-semibold rounded px-2 py-1 h-[22px] center',
          statusDisplay().color,
          statusDisplay().bgColor
        )}
      >
        {statusDisplay().text}
      </div>
    </div>
  );
};

/**
 * 比赛信息卡片组件 - 所有订单类型共用
 * 显示比赛时间、联赛信息、队伍信息、投注项
 * 支持可选的选择项状态显示（用于 Combo/System）
 */
export const MatchInfoCard = (props: {
  selection: BetSelection;
  oddsValue?: string | number;
  class?: string;
  /** 是否显示选择项状态标签 */
  showSelectionStatus?: boolean;
  /** 顶部插槽内容（如 Selection 编号标签） */
  topSlot?: any;
  ticketType?: string;
  variant?: 'openbets' | 'mybets';
  showBorder?: boolean;
  betType?: string;
}) => {
  const matchDetail = useOptimizedMatchDetail(() => props.selection.matchId);
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = createSignal(false);

  const selectionStatusDisplay = () => getStatusDisplay(props.selection.settledStatus as SettledStatus);

  createEffect(() => {
    console.log('MatchDetail updated:', props.ticketType);
  });

  // 判断是否是直播比赛
  const isLive = () => {
    const detail = matchDetail();
    if (detail && checkEventStatus(detail.status as EventStatus).isLive) {
      return true;
    }
    return false;
  };

  const jumpToMatch = () => {
    if (props.ticketType === SingleSubType.Outright) {
      const { matchId, sportId, sportName, matchName } = props.selection;
      if (!matchId) return;
      navigate(`/outright/${matchId}`, {
        state: {
          from: 'mybets',
          sport: sportName,
          seasonName: matchName,
          sportId,
        },
      });
      return;
    }

    const detail = matchDetail();
    if (!detail) return;
    navigate(`/match/${detail.id}`);
  };

  const showStatistics = (e: MouseEvent) => {
    e.stopPropagation();
    const _sportId = toTrailingDigits(matchDetail()?.sportId);
    const _seasonId = toTrailingDigits(matchDetail()?.seasonId);
    const _homeId = toTrailingDigits(matchDetail()?.homeId);
    const _awayId = toTrailingDigits(matchDetail()?.awayId);
    const _matchId = toTrailingDigits(matchDetail()?.id);
    const _lang = getSportsRadarLang(setting.lang);
    window.open(
      `https://s5.sir.sportradar.com/sblive/${_lang}/${_sportId}/season/${_seasonId}/h2h/${_homeId}/${_awayId}/match/${_matchId}`
    );
  };

  const selection = createMemo(() => {
    const selection = props.selection;
    replaceOutcomesPlaceholders([selection], selection.homeName, selection.awayName);
    return selection;
  });

  return (
    <div
      class={cx(
        'relative flex flex-col gap-0 rounded-lg bg-mybet-card-body-bg  cursor-pointer group',
        props.variant === 'openbets' ? 'p-2 pb-0 pt-0 last:pb-2' : 'p-3',
        props.class
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={jumpToMatch}
    >
      {/* 顶部插槽（如 Selection 编号） */}
      {props.topSlot}
      <div class="relative">
        {/* 比赛时间 + 选择项状态（可选） */}
        <div class={cx('flex items-center gap-2.5 cursor-pointer', props.showSelectionStatus && 'justify-between')}>
          <div class="flex flex-1 items-center gap-1 cursor-pointer">
            <Show
              when={isLive()}
              fallback={
                <>
                  <Show when={matchDetail()}>
                    <div class="flex items-center gap-1 text-left">
                      <MatchTimeDisplay matchDetail={matchDetail() as unknown as SportCategoryMatch} />
                    </div>
                  </Show>
                </>
              }
            >
              <div class="flex items-center justify-between gap-1 w-full">
                <Show when={matchDetail()}>
                  <div class="flex items-center gap-1 text-left">
                    <MatchTimeDisplay matchDetail={matchDetail() as unknown as SportCategoryMatch} />
                  </div>
                </Show>
                <IconLive class="size-4 text-function-live " />
              </div>
            </Show>
          </div>

          {/* 选择项状态标签（可选） */}
          <Show when={props.showSelectionStatus}>
            <div
              class={cx(
                'caption-s-semibold rounded px-2 py-1',
                selectionStatusDisplay().color,
                selectionStatusDisplay().bgColor
              )}
            >
              {selectionStatusDisplay().text}
            </div>
          </Show>
        </div>
        {/* 联赛信息 */}
        <div class="caption-s-medium flex h-6 items-center gap-0.5 text-neutral-main2 cursor-pointer group-hover:text-neutral-main1">
          <SportIcon
            class="text-neutral-main2 cursor-pointer size-3.5"
            sportId={props.selection.sportId}
            type={isHovered() ? 'solid' : 'default'}
          />
          <span class="truncate cursor-pointer">{props.selection.basicCountry}</span>
          <span class="cursor-pointer">,</span>
          {/* <IconArrow04 class="size-4 -rotate-90" /> */}
          <span class="truncate cursor-pointer">{props.selection.tournamentName}</span>
        </div>
        {/* 队伍信息 */}
        <Show when={props.ticketType !== 'OUTRIGHT'}>
          <div class={cx('flex h-8 items-center justify-between gap-1 overflow-hidden cursor-pointer')}>
            <div class="block max-w-[250px] min-w-0 truncate">
              <Tooltip content={`${props.selection.homeName} vs ${props.selection.awayName}`}>
                <div class="flex items-center gap-1">
                  <span
                    class={cx(
                      'text-neutral-main1 cursor-pointer',
                      props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
                    )}
                  >
                    {props.selection.homeName || ''}
                  </span>
                  <span
                    class={cx(
                      'text-neutral-main2 shrink-0 cursor-pointer',
                      props.variant === 'openbets' ? 'caption-s-medium' : 'caption-m-medium'
                    )}
                  >
                    {t('vs')}
                  </span>
                  <span
                    class={cx(
                      'text-neutral-main1 cursor-pointer',
                      props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
                    )}
                  >
                    {props.selection.awayName || ''}
                  </span>
                </div>
              </Tooltip>
            </div>
            <Show when={matchDetail()?.status !== EventStatus.LIVE_EVENTS}>
              <Tooltip content={t('Statistics')}>
                <SvgIcon
                  icon={IconLivingStatistic}
                  onClick={(e) => showStatistics(e)}
                  class={cx('size-5 text-neutral-main5 hover:text-neutral-main2 transition-all cursor-pointer')}
                />
              </Tooltip>
            </Show>
          </div>
        </Show>
        {/* <IconGame
          class={cx('size-4 text-neutral-main2 absolute right-0', props.ticketType === 'OUTRIGHT' ? 'top-6' : 'top-11')}
        /> */}
      </div>
      {/* 投注项 - CustomBet 类型不显示 */}
      <Show
        when={props.ticketType !== SingleSubType.CustomBet}
        fallback={
          <TotalOddsRow totalOdds={Number(props.oddsValue || props.selection.oddsValue)} variant={props.variant} />
        }
      >
        <div class="flex items-center justify-between cursor-pointer mb-3 last:mb-0">
          <div class="flex gap-1 flex-1 truncate cursor-pointer flex-col">
            <Show when={(props.selection as any).colName || props.selection.marketName}>
              <span class="flex items-center gap-0.5 rounded px-0.5 flex-1 overflow-hidden cursor-pointer">
                <span
                  class={cx(
                    'text-neutral-main2 truncate cursor-pointer !text-[14px]',
                    props.variant === 'openbets' ? 'caption-s-medium' : 'caption-m-medium'
                  )}
                >
                  {(props.selection as any).colName ? `${(props.selection as any).colName} ` : ''}
                  {props.selection.marketName}
                </span>
              </span>
            </Show>
            <div class="flex justify-between">
              <span
                class={cx(
                  'text-neutral-main1 cursor-pointer !text-[16px]',
                  props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
                )}
              >
                {selection().outcomeName || ''}
              </span>
              <span
                class={cx(
                  'ml-4 cursor-pointer',
                  props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold',
                  props.betType === BetSlipType.Single ? 'text-function-open' : 'text-neutral-main1'
                )}
              >
                <OddsFormat value={props.oddsValue ? Number(props.oddsValue) : Number(props.selection.oddsValue)} />
              </span>
            </div>
          </div>
        </div>
      </Show>
      <Show when={props.showBorder}>
        <div class={cx('border-b', props.variant === 'mybets' ? 'border-main3' : 'border-main1-auto')} />
      </Show>
    </div>
  );
};

/**
 * Ticket ID 行组件
 */
export const TicketIdRow = (props: { ticketNo: string; variant?: 'openbets' | 'mybets' }) => {
  return (
    <div
      class="flex items-center justify-between h-8 cursor-pointer group"
      ref={(el) => {
        if (el) {
          copyToClipboard(el, { value: props.ticketNo });
        }
      }}
      onClick={() => toast.success(t('Copied'))}
    >
      <div class="flex items-center cursor-pointer">
        <span
          class={cx(
            'text-neutral-main2 cursor-pointer',
            props.variant === 'openbets' ? 'caption-s-medium' : 'caption-m-medium'
          )}
        >
          {t('Ticket ID:')}
        </span>
        <span
          class="flex cursor-pointer items-center gap-1 px-1"
          // ref={(el) => {
          //   if (el) {
          //     copyToClipboard(el, { value: props.ticketNo });
          //   }
          // }}
          // onClick={() => toast.success(t('Copied'))}
        >
          <span
            class={cx(
              'text-neutral-main1 cursor-pointer',
              props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
            )}
          >
            {props.ticketNo}
          </span>
          <IconCopy
            class={cx(
              'size-4 text-neutral-main2 cursor-pointer group-hover:text-neutral-main1',
              props.variant === 'openbets' ? 'size-3' : 'size-4'
            )}
          />
        </span>
      </div>
      <Show when={props.variant !== 'mybets'}>
        <IconIcon class="size-4" />
      </Show>
    </div>
  );
};

export const TotalOddsRow = (props: { totalOdds: number; variant?: 'openbets' | 'mybets'; labelColor?: string }) => {
  return (
    <div class="flex items-center justify-between cursor-pointer h-6">
      <div class="flex items-center">
        <span
          class={cx(
            'text-neutral-main1 cursor-pointer',
            props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold',
            props.labelColor ? props.labelColor : ''
          )}
        >
          {t('Total Odds')}
        </span>
      </div>
      <span
        class={cx(
          'cursor-pointer text-function-open',
          props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
        )}
      >
        <OddsFormat value={props.totalOdds} />
      </span>
    </div>
  );
};

/**
 * Stake 行组件
 */
export const StakeRow = (props: {
  stakeAmount: number;
  realAmount: number;
  creditAmount: number;
  ticketCurrency: string;
  paymentMethod: 'BALANCE' | 'BONUS';
  variant?: 'openbets' | 'mybets';
}) => {
  const { latestCurrency: _latestCurrency } = useCurrency();
  const [isTotalDetailOpen, setIsTotalDetailOpen] = createSignal(false);
  const isCostCredit = createMemo(() => props.creditAmount > 0);
  const realAmountDec = createMemo(() => new Decimal(props.realAmount));
  // const isCostCredit = createMemo(() => props.stakeAmount - props.realAmount > 0)
  return (
    <div>
      <div class="flex items-center justify-between h-8">
        <span
          class={cx(
            'text-neutral-main2',
            props.variant === 'openbets' ? 'caption-s-medium' : 'caption-m-medium !font-bold'
          )}
        >
          {t('Total Stake')}
        </span>
        <div class="flex flex-col items-end">
          <div class="flex items-center gap-[2px]">
            <Show when={isBonus(props.paymentMethod)}>
              <span class="center bg-alpha-bw-autowhite-a10 text-neutral-main1 text-[10px] font-[600] rounded-2xl h-5 px-[6px]">
                {t('Bonus')}
              </span>
            </Show>
            <img class="size-4 rounded-full" src={getCoinIcon(props.ticketCurrency)} alt={props.ticketCurrency} />
            <span
              class={cx(
                'text-neutral-main1 !font-bold',
                props.variant === 'openbets' ? 'caption-s-medium' : 'caption-m-semibold'
              )}
            >
              {currencyConvertHint(props.stakeAmount, props.ticketCurrency, props.ticketCurrency, 'transaction')}
            </span>
            <Show when={isCostCredit()}>
              <IconArrow06
                class={cx(
                  'size-4 cursor-pointer text-neutral-main3 transition-transform',
                  isTotalDetailOpen() ? 'rotate-180' : ''
                )}
                onClick={() => setIsTotalDetailOpen(!isTotalDetailOpen())}
              />
            </Show>
          </div>
          {/* 汇率转换提示 */}

          <Show when={!isFiat(props.ticketCurrency) && props.ticketCurrency !== walletSettings.showCurrency}>
            <div class="flex gap-1">
              <span class="text-[12px] font-normal text-neutral-main3">
                ≈{currencyConvertHint(props.stakeAmount, props.ticketCurrency, walletSettings.showCurrency, 'display')}
              </span>
              {/* 占位样式对齐 */}
              <Show when={isCostCredit()}>
                <div class="inline size-4" />
              </Show>
            </div>
          </Show>
        </div>
      </div>
      <Toggle when={isCostCredit() && isTotalDetailOpen()}>
        <div class="bg-layer3 rounded-[4px] p-2 mb-2">
          <div class="flex items-center justify-between ">
            <div class="text-neutral-main5">{t('Cash')}</div>
            <div class="flex items-center">
              <img class="size-3 mr-1" src={getCoinIcon(props.ticketCurrency)} alt={props.ticketCurrency} />
              {currencyConvertHint(realAmountDec(), props.ticketCurrency, props.ticketCurrency, 'transaction')}
            </div>
          </div>
          <div class="flex items-center justify-between mt-2">
            <div class="text-neutral-main5">{t('VIP Credits')}</div>
            <div class="flex items-center">
              <img class="size-3 mr-1" src={getCoinIcon(props.ticketCurrency)} alt={props.ticketCurrency} />
              {currencyConvertHint(props.creditAmount, props.ticketCurrency, props.ticketCurrency, 'transaction')}
            </div>
          </div>
        </div>
      </Toggle>
    </div>
  );
};

/**
 * Potential Win / Result 行组件
 */
export const ResultRow = (props: { ticket: BetTicket; variant?: 'openbets' | 'mybets' }) => {
  const { latestCurrency: _latestCurrency } = useCurrency();

  const getLabel = () => {
    if (props.ticket.settledStatus === SettledStatus.OPEN) {
      return t('Est. Return');
    }
    if (props.ticket.settledStatus === SettledStatus.REFUND) {
      return t('Refund');
    }
    if (props.ticket.settledStatus === SettledStatus.WIN || props.ticket.settledStatus === SettledStatus.HALF_WIN) {
      return t('Return');
    }
    if (props.ticket.settledStatus === SettledStatus.CASHED_OUT) {
      return t('Return');
    }
    if (props.ticket.settledStatus === SettledStatus.PARTIAL_RETURN) {
      return t('Return');
    }
    return t('Return');
  };

  const getAmount = () => {
    if (props.ticket.settledStatus === SettledStatus.CASHED_OUT) {
      return props.ticket.cashedOutAmount;
    }
    if (
      props.ticket.settledStatus === SettledStatus.WIN ||
      props.ticket.settledStatus === SettledStatus.HALF_WIN ||
      props.ticket.settledStatus === SettledStatus.PARTIAL_RETURN
    ) {
      return props.ticket.winAmount;
    }
    return props.ticket.potentialPayout;
  };

  return (
    <Show
      when={
        props.ticket.settledStatus !== SettledStatus.LOSE &&
        props.ticket.settledStatus !== SettledStatus.CANCELLED &&
        props.ticket.settledStatus !== SettledStatus.REFUND &&
        !(props.ticket.settledStatus === SettledStatus.PARTIAL_RETURN && props.ticket.winAmount <= 0)
      }
    >
      <div class="flex items-center justify-between h-8">
        <span
          class={cx('text-neutral-main1', props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold')}
        >
          {getLabel()}
        </span>
        <div class="flex flex-col items-end">
          <div class="flex items-center gap-[2px]">
            <img class="size-4 rounded-full" src={getCoinIcon(props.ticket.currency)} alt={props.ticket.currency} />
            <span
              class={cx(
                'text-neutral-main1 !font-bold',
                props.variant === 'openbets' ? 'caption-s-semibold' : 'caption-m-semibold'
              )}
            >
              {/* {currency.printCurrency(getAmount(), props.ticket.currency, {
              target: props.ticket.currency,
              precision: 100,
            })} */}
              {currencyConvertHint(getAmount(), props.ticket.currency, props.ticket.currency, 'transaction')}
            </span>
          </div>

          <Show when={!isFiat(props.ticket.currency) && props.ticket.currency !== walletSettings.showCurrency}>
            <span class="text-[12px] font-normal text-neutral-main3">
              ≈{/* {currency.getCurrencySymbol(walletSettings.showCurrency)} */}
              {/* {currency.printCurrency(getAmount(), props.ticket.currency, {
                target: walletSettings.showCurrency,
                precision: 2,
              })} */}
              {currencyConvertHint(getAmount(), props.ticket.currency, walletSettings.showCurrency, 'transaction')}
            </span>
          </Show>
        </div>
      </div>
    </Show>
  );
};

/**
 * Cash Out 按钮组件
 */
export const CashOutButton = (props: {
  ticket: BetTicket;
  canCashOutMoney?: number | (() => number | undefined);
  onCashOut?: () => void;
  disabled?: boolean;
  /** 是否隐藏按钮（当查询返回不可用时设为 true） */
  hidden?: boolean;
  /** 是否正在执行 cashout（点击按钮后的 loading 状态） */
  isCashing?: boolean | (() => boolean);
}) => {
  // 响应式获取金额（支持直接值或函数）
  const amount = () => (typeof props.canCashOutMoney === 'function' ? props.canCashOutMoney() : props.canCashOutMoney);

  // 响应式获取 cashing 状态（支持直接值或函数）
  const isCashing = () => (typeof props.isCashing === 'function' ? props.isCashing() : props.isCashing);

  // 是否正在加载金额（金额未知但未被隐藏）
  const isLoadingAmount = createMemo(() => amount() === undefined && !props.hidden);

  // 是否显示 loading：正在加载金额或正在执行 cashout
  const isLoading = createMemo(() => isLoadingAmount() || isCashing());

  // 是否应该隐藏按钮：明确被隐藏，或者金额小于0.01
  const shouldHide = () => props.hidden || (amount() ?? 0) < 0.01 || isBonus(props.ticket?.paymentMethod);

  return (
    <Show when={props.ticket.settledStatus === SettledStatus.OPEN && !shouldHide()}>
      <Button
        class="button-brand w-full text-neutral-alw-black h-10 font-semibold"
        onClick={() => props.onCashOut?.()}
        disabled={props.disabled || isLoading()}
      >
        <Show
          when={!isLoading()}
          fallback={
            <>
              <Loading class="text-alw_white" />
            </>
          }
        >
          <span class="caption-m-semibold flex items-center gap-1">
            <span>{t('Cash Out')}</span>
            <span>
              {currencyConvertHint(amount() ?? 0, props.ticket.currency, props.ticket.currency, 'transaction')}
            </span>
          </span>
        </Show>
      </Button>
    </Show>
  );
};

// 当该笔订单查询出来的可兑现金额为0时使用，或者查询出来的结果异常的时候进行使用
export const DisabledCashOutButton = (props: { ticket: BetTicket }) => {
  return (
    <Show when={props.ticket.settledStatus === SettledStatus.OPEN}>
      <Button type="second" class=" w-full  h-10 " disabled={true}>
        {t('Cash Out Not Available')}
      </Button>
    </Show>
  );
};
/**
 * 卡片容器组件
 */
export const BetCardContainer = (props: {
  children: any;
  /** 自定义类名，可以覆盖默认样式 */
  class?: string;
}) => {
  return (
    <div
      class={cx('flex flex-col gap-2 rounded-lg bg-layer5 p-3 mb-2 mt-2 mr-3 ml-3', props.class)}
      style={{ 'box-shadow': '0px 3px 12px rgba(0, 12, 20, 0.04)' }}
    >
      {props.children}
    </div>
  );
};

/**
 * 金额汇总卡片容器
 */
export const AmountSummaryCard = (props: { children: any; class?: string }) => {
  return (
    <div class={cx('flex flex-col justify-center rounded-lg bg-mybet-card-body-bg p-3', props.class)}>
      {props.children}
    </div>
  );
};

/**
 * 收起状态下的选择项预览进度条
 * 每个选择项用一个颜色块表示其状态
 */
export const SelectionPreviewBar = (props: { selections: BetSelection[] }) => {
  // 获取状态对应的颜色
  const getStatusColor = (status: number) => {
    switch (status) {
      case SettledStatus.OPEN:
        return '#409FFA'; // 蓝色
      case SettledStatus.WIN:
      case SettledStatus.HALF_WIN:
        return '#00DC93'; // 绿色
      case SettledStatus.LOSE:
      case SettledStatus.HALF_LOSE:
        return '#F4301C'; // 红色
      case SettledStatus.CANCELLED:
      case SettledStatus.PUSH:
        return '#A3BECC'; // 灰色
      case SettledStatus.CASHED_OUT:
        return '#FACC15'; // 黄色
      default:
        return '#A3BECC';
    }
  };

  return (
    <div class="flex h-[2px] w-full gap-1 p-0 relative ">
      <For each={props.selections}>
        {(selection) => (
          <div class="flex-1 h-0.5" style={{ 'background-color': getStatusColor(selection.settledStatus) }} />
        )}
      </For>
    </div>
  );
};
